"use client";

type ReferralCodeSource = "url" | "localStorage" | null;

/**
 * Sanitize referral code by stripping any query params accidentally appended
 * e.g. "TRAA7JMP?utm=danpullo" → "TRAA7JMP" (user shared ?code=X?utm=Y instead of &utm=Y)
 */
const sanitizeReferralCode = (code: string): string => {
  const questionMarkIndex = code.indexOf("?");
  return questionMarkIndex !== -1 ? code.substring(0, questionMarkIndex) : code;
};

/**
 * Get referral code and its source from URL or localStorage (SSR-safe)
 */
export const getReferralCodeWithSource = (): { code: string | null; source: ReferralCodeSource } => {
  if (typeof window === "undefined") return { code: null, source: null };

  const codeFromUrl = new URLSearchParams(window.location.search).get("code");
  if (codeFromUrl) {
    const sanitized = sanitizeReferralCode(codeFromUrl);
    // Persist to localStorage so the code survives navigation away from the referral URL
    localStorage.setItem("pendingReferralCode", sanitized);
    return { code: sanitized, source: "url" };
  }

  const codeFromStorage = localStorage.getItem("pendingReferralCode");
  if (codeFromStorage) return { code: sanitizeReferralCode(codeFromStorage), source: "localStorage" };

  return { code: null, source: null };
};

/**
 * Get referral code from URL or localStorage (SSR-safe)
 */
export const getReferralCodeFromSources = (): string | null => {
  return getReferralCodeWithSource().code;
};

export const clearPendingReferralCode = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem("pendingReferralCode");
};

export const hasReferralCode = () => !!getReferralCodeFromSources();
